import { useEffect, useMemo, useState } from "react";
import { useAllOrganizations, useAuthenticatedUser, useSubscriptionOrganizations, useTeams } from "../../../../store";
import { useAppContext } from "../../../hooks/useAppContext";
import { useCountryConfig } from "../../../hooks/useCountryConfig";
import { useI18n } from "../../../hooks/useI18n";
import { useQueries } from "../../../providers/Dependencies";
import { buildImportDestinationOptions, getPreferredImportDestinationId } from "../evcImportDestinations";

export function useImportDestination() {
  const { t } = useI18n();
  const queries = useQueries();
  const { allOrganizationsQuery } = queries;
  const currentUser = useAuthenticatedUser();
  const organizations = useAllOrganizations();
  const subscriptionOrganizations = useSubscriptionOrganizations();
  const teams = useTeams();
  const { organizationId, teamId } = useAppContext();
  const { subscription } = useCountryConfig();
  const [selectedDestinationId, setSelectedDestinationId] = useState("");

  useEffect(() => {
    allOrganizationsQuery.call().catch(() => undefined);
  }, [allOrganizationsQuery]);

  useEffect(() => {
    if (subscription) {
      subscription.queries.subscriptionOrganizationsQuery.call().catch(() => undefined);
    }
  }, [subscription]);

  const importDestinations = useMemo(
    () =>
      buildImportDestinationOptions({
        organizations,
        subscriptionOrganizations,
        teams,
        canImportToLiberal: !!currentUser.rpps,
        liberalLabel: t("evc_onboarding.destination_liberal"),
      }),
    [currentUser.rpps, organizations, subscriptionOrganizations, t, teams],
  );

  const selectedDestination = useMemo(
    () => importDestinations.find((destination) => destination.id === selectedDestinationId) ?? null,
    [importDestinations, selectedDestinationId],
  );

  useEffect(() => {
    if (organizationId && teamId && (!organizations || !teams || (subscription && !subscriptionOrganizations))) {
      return;
    }

    const hasValidSelection = importDestinations.some((destination) => destination.id === selectedDestinationId);
    if (hasValidSelection) {
      return;
    }

    setSelectedDestinationId(
      getPreferredImportDestinationId({
        destinations: importDestinations,
        currentOrganizationId: organizationId,
        currentTeamId: teamId,
      }),
    );
  }, [
    importDestinations,
    organizationId,
    organizations,
    selectedDestinationId,
    subscription,
    subscriptionOrganizations,
    teamId,
    teams,
  ]);

  return {
    importDestinations,
    selectedDestination,
    selectedDestinationId,
    setSelectedDestinationId,
  };
}
